import { useEffect, useState } from "react";
import { getGeoPoint, getGeoPolygon } from "../../../common";
import { getFormData, getQueryData, getQueryString } from '../../../common/FormUtil';
import { searchComplexAreaInComplexV4, searchEducationsV2, searchEnvironmentsV2, searchFrontYards, searchLivingAreas } from '../../../repository/RealEstateRepository';
import { Navbar } from "../molecule";
import { ComplexAreaMap, FrontYardLivingAreaForm } from "../organism";

const ComplexAreaMapPage = (props) => {
  const [selectedFrontYardId, setSelectedFrontYardId] = useState(null);
  const [selectedLivingAreaId, setSelectedLivingAreaId] = useState(null);
  const [landmarkSelected, setLandmarkSelected] = useState(false);
  const [bookmarkSelected, setBookmarkSelected] = useState(false);
  const [complexList, setComplexList] = useState([]);
  const [frontYardList, setFrontYardList] = useState([]);
  const [livingAreaList, setLivingAreaList] = useState([]);
  const [educations, setEducations] = useState(null);
  const [environments, setEnvironments] = useState(null);
  const [bgLivingAreaList, setBgLivingAreaList] = useState([]);
  const [viewLivingArea, setViewLivingArea] = useState(null);
  const [filterList, setFilterList] = useState([]);
  const [mapBounds, setMapBounds] = useState(null);
  const [locationSearch, setLocationSearch] = useState(null);
  const [onLoad, setOnLoad] = useState(false);

  // general form 반환
  const getCurrentForm = (form = {}) => ({
    frontYardId: selectedFrontYardId,
    livingAreaId: selectedLivingAreaId,
    livingAreaIds: bgLivingAreaList.map(v => v.id).join(","),
    landmarkOnly: landmarkSelected,
    bookmarkOnly: bookmarkSelected,
    filters: filterList.join(","),
    ...mapBounds,
    ...form
  });

  // boolean은 해당 컴포넌트에서 사용, enum은 서버 사용
  const getFilterList = (data = {}) => {
    const filters = [];
    if (data.middleSchool) filters.push("MIDDLE_SCHOOL");
    if (data.elementarySchool) filters.push("ELEMENTARY_SCHOOL");
    if (data.academy) filters.push("ACADEMY_STREET");
    if (data.departmentStore) filters.push("DEPARTMENT_STORE");
    if (data.market) filters.push("MARKET");
    if (data.hospital) filters.push("HOSPITAL");
    if (data.starbucks) filters.push("STARBUCKS");
    if (data.education) {
      filters.push("MIDDLE_SCHOOL");
      filters.push("ELEMENTARY_SCHOOL");
      filters.push("ACADEMY_STREET");
    };
    if (data.environment) {
      filters.push("DEPARTMENT_STORE");
      filters.push("MARKET");
      filters.push("HOSPITAL");
      filters.push("STARBUCKS");
      filters.push("CUSTOM");
    }
    return filters;
  };

  useEffect(() => {
    searchFrontYards({}, (res) => setFrontYardList(res.content));
  }, []);

  useEffect(() => {
    //INIT
    const { frontYardId, livingAreaId, landmarkOnly, bookmarkOnly, filters } = getQueryData(props.location.search);
    //VIEW
    setSelectedFrontYardId(frontYardId || "");
    setSelectedLivingAreaId(livingAreaId || "");
    setLandmarkSelected((landmarkOnly === undefined) ? true : (landmarkOnly === "true"));
    setBookmarkSelected((bookmarkOnly === undefined) ? false : (bookmarkOnly === "true"));
    setFilterList((filters || "").split(","));
    //ON_CHANGE_URL
    setLocationSearch(props.location.pathname + props.location.search);
    //ON_LOAD
    setTimeout(() => setOnLoad(true));
    // eslint-disable-next-line
  }, [props.location]);

  // 1. mapBoundChanged
  const onChangeMapBounds = (map) => {
    setMapBounds({
      swLat: map.bounds.getSW().lat(),
      swLng: map.bounds.getSW().lng(),
      neLat: map.bounds.getNE().lat(),
      neLng: map.bounds.getNE().lng(),
      centerLat: map.center.lat(),
      centerLng: map.center.lng()
    });
  };

  // 2. nearLivingAreaChanged
  useEffect(() => {
    if (!mapBounds) return;
    const _p = getGeoPoint(mapBounds.centerLng, mapBounds.centerLat);
    searchLivingAreas(getCurrentForm({ withBoundary: true }), (res) => {
      res.content.forEach(v => {
        if (!v.boundary) return;
        const _pgn = getGeoPolygon(v.boundary.map(c => ({ x: c[0], y: c[1] })));
        v.selected = _pgn && _p.within(_pgn);
      });
      setViewLivingArea(res.content.filter(v => v.selected)[0]);
      const prev = bgLivingAreaList.map(v => `${v.id}${v.selected ? 'V' : ''}`).join(",");
      const next = res.content.map(v => `${v.id}${v.selected ? 'V' : ''}`).join(",");
      if (prev !== next)
        setBgLivingAreaList(res.content);
    });
    // eslint-disable-next-line
  }, [mapBounds]);

  // 3. mapChangedForData
  useEffect(() => {
    if (!locationSearch) return;
    if (!bgLivingAreaList.length) return;

    searchComplexAreaInComplexV4(getCurrentForm({}), (res) => setComplexList(res.content));
    const _edu = ["MIDDLE_SCHOOL", "ELEMENTARY_SCHOOL", "ACADEMY_STREET"];
    const _env = ["DEPARTMENT_STORE", "MARKET", "HOSPITAL", "STARBUCKS", "CUSTOM"];
    searchEducationsV2({ types: filterList.filter(v => _edu.includes(v)).join(","), ...mapBounds }, (res) => setEducations(res));
    searchEnvironmentsV2({ types: filterList.filter(v => _env.includes(v)).join(","), ...mapBounds }, (res) => setEnvironments(res.content));
    // eslint-disable-next-line
  }, [bgLivingAreaList, locationSearch]);

  useEffect(() => {
    if (!selectedFrontYardId) {
      setLivingAreaList([]);
    }
    if (selectedFrontYardId) {
      searchLivingAreas({ frontYardId: selectedFrontYardId }, (res) => setLivingAreaList(res.content));
    }
  }, [selectedFrontYardId]);

  return (
    <>
      <Navbar pathname={props.location.pathname} />

      {onLoad && (<>
        <ComplexAreaMap
          reloadComplexArea={() => {
            searchComplexAreaInComplexV4(getCurrentForm(), (res) => setComplexList(res.content));
          }}
          onChangeMapBounds={onChangeMapBounds}
          bgLivingAreaList={bgLivingAreaList}
          viewLivingArea={viewLivingArea}
          educations={educations}
          environments={environments}
          complexList={complexList} />
      </>)}

      <div style={{ position: "fixed", top: 56, padding: 10, background: "#fff", borderBottom: "1px solid #000", left: 0, right: 0 }}>
        <FrontYardLivingAreaForm
          frontYards={frontYardList}
          livingAreas={livingAreaList}
          selectedFrontYardId={selectedFrontYardId}
          selectedLivingAreaId={selectedLivingAreaId}
          onChangeFrontYard={(e) => {
            const formData = getFormData(e.target.form);
            const request = getCurrentForm(formData);
            if (!formData.frontYardId) delete request.frontYardId;
            delete request.livingAreaId;
            props.history.push(`?${getQueryString(request)}`);
          }}
          onChangeLivingArea={(e) => {
            const formData = getFormData(e.target.form);
            const request = getCurrentForm(formData);
            if (!formData.livingAreaId) delete request.livingAreaId;
            props.history.push(`?${getQueryString(request)}`);
          }}
        />
      </div>

      {viewLivingArea && <>
        <div style={{ position: "fixed", top: 118, left: 0, right: 0, height: 0 }}>
          <div
            style={{ display: "inline-block", background: "#fff", padding: "2px 8px 0px", border: "1px solid #777", borderRadius: "1em" }}>
            {viewLivingArea.frontYardName} / {viewLivingArea.name}({viewLivingArea.weight})
          </div>
        </div>
      </>}

      {onLoad && <>
        <div style={{ position: "fixed", top: 108 + 4, right: 4 }}>
          <ComplexFilterLayer
            landmarkSelected={landmarkSelected}
            bookmarkSelected={bookmarkSelected}
            onChange={(e, formData) => {
              props.history.push(`?${getQueryString(getCurrentForm(formData))}`);
            }} />
          <CircumstanceFilterLayer
            filterList={filterList}
            onChange={(form) => {
              props.history.push(`?${getQueryString(getCurrentForm({ filters: getFilterList(form).join(",") }))}`);
            }} />
        </div>
      </>}
    </>
  );
};

const ComplexFilterLayer = (props) => {
  return (
    <form>
      <OnOff name="landmarkOnly" value={props.landmarkSelected} desc="랜드" onClick={(e) => {
        e.preventDefault();
        const formData = getFormData(e.target.form);
        formData.landmarkOnly = !props.landmarkSelected;
        props.onChange(e, formData);
      }} />
      <OnOff name="bookmarkOnly" value={props.bookmarkSelected} desc="북맠"
        style={{ marginTop: 2 }}
        onClick={(e) => {
          e.preventDefault();
          const formData = getFormData(e.target.form);
          formData.bookmarkOnly = !props.bookmarkSelected;
          props.onChange(e, formData);
        }} />
    </form>
  );
};

const CircumstanceFilterLayer = (props) => {
  const { filterList } = props;
  const [academy, setAcademy] = useState(filterList.includes("ACADEMY_STREET"));
  const [middleSchool, setMiddleSchool] = useState(filterList.includes("MIDDLE_SCHOOL"));
  const [elementarySchool, setElementarySchool] = useState(filterList.includes("ELEMENTARY_SCHOOL"));
  const [departmentStore, setDepartmentStore] = useState(filterList.includes("DEPARTMENT_STORE"));
  const [market, setMarket] = useState(filterList.includes("MARKET"));
  const [hospital, setHospital] = useState(filterList.includes("HOSPITAL"));
  const [starbucks, setStarbucks] = useState(filterList.includes("STARBUCKS"));

  const [education, setEducation] = useState(filterList.includes("ACADEMY_STREET") && filterList.includes("MIDDLE_SCHOOL") && filterList.includes("ELEMENTARY_SCHOOL"));
  const [environment, setEnvironment] = useState(filterList.includes("DEPARTMENT_STORE") && filterList.includes("MARKET") && filterList.includes("HOSPITAL") && filterList.includes("STARBUCKS") && filterList.includes("CUSTOM"));

  const onClickAcademy = (e) => {
    e.preventDefault();
    const formData = getFormData(e.target.form);
    formData.academy = !academy;
    setAcademy(!academy);
    props.onChange && props.onChange(formData);
  };

  const onClickMiddleSchool = (e) => {
    e.preventDefault();
    const formData = getFormData(e.target.form);
    formData.middleSchool = !middleSchool;
    setMiddleSchool(!middleSchool);
    props.onChange && props.onChange(formData);
  };

  const onClickElementarySchool = (e) => {
    e.preventDefault();
    const formData = getFormData(e.target.form);
    formData.elementarySchool = !elementarySchool;
    setElementarySchool(!elementarySchool);
    props.onChange && props.onChange(formData);
  };

  const onClickDepartmentStore = (e) => {
    e.preventDefault();
    const formData = getFormData(e.target.form);
    formData.departmentStore = !departmentStore;
    setDepartmentStore(!departmentStore);
    props.onChange && props.onChange(formData);
  };

  const onClickMarket = (e) => {
    e.preventDefault();
    const formData = getFormData(e.target.form);
    formData.market = !market;
    setMarket(!market);
    props.onChange && props.onChange(formData);
  };

  const onClickHospital = (e) => {
    e.preventDefault();
    const formData = getFormData(e.target.form);
    formData.hospital = !hospital;
    setHospital(!hospital);
    props.onChange && props.onChange(formData);
  };

  const onClickStarbucks = (e) => {
    e.preventDefault();
    const formData = getFormData(e.target.form);
    formData.starbucks = !starbucks;
    setStarbucks(!starbucks);
    props.onChange && props.onChange(formData);
  };

  const onClickEducation = (e) => {
    e.preventDefault();
    const formData = getFormData(e.target.form);
    formData.education = !education;
    setEducation(!education);
    props.onChange && props.onChange(formData);
  };

  const onClickEnvironment = (e) => {
    e.preventDefault();
    const formData = getFormData(e.target.form);
    formData.environment = !environment;
    setEnvironment(!environment);
    props.onChange && props.onChange(formData);
  };

  return (
    <>
      <form>
        {false && <>
          <OnOff name="academy" value={academy} desc="학원" onClick={onClickAcademy} style={{ marginTop: 2 }} />
          <OnOff name="middleSchool" value={middleSchool} desc="중학" onClick={onClickMiddleSchool} style={{ marginTop: 2 }} />
          <OnOff name="elementarySchool" value={elementarySchool} desc="초등" onClick={onClickElementarySchool} style={{ marginTop: 2 }} />
          <OnOff name="departmentStore" value={departmentStore} desc="백화" onClick={onClickDepartmentStore} style={{ marginTop: 2 }} />
          <OnOff name="market" value={market} desc="마트" onClick={onClickMarket} style={{ marginTop: 2 }} />
          <OnOff name="hospital" value={hospital} desc="병원" onClick={onClickHospital} style={{ marginTop: 2 }} />
          <OnOff name="starbucks" value={starbucks} desc="스벅" onClick={onClickStarbucks} style={{ marginTop: 2 }} />
        </>}
        <OnOff name="education" value={education} desc="학군" onClick={onClickEducation} style={{ marginTop: 2 }} />
        <OnOff name="environment" value={environment} desc="환경" onClick={onClickEnvironment} style={{ marginTop: 2 }} />
      </form>
    </>
  );
};

const OnOff = (props) => (
  <div style={{ ...props.style }}>
    <input type="hidden" name={props.name} value={props.value} readOnly={true} />
    <button className={`btn ${props.value ? "btn-primary" : "btn-light"}`} onClick={props.onClick}>{props.desc}</button>
  </div>
);

export { ComplexAreaMapPage };
